import AccountLayout from "@/layouts/AccountLayout.vue";
import AppLayout from "@/layouts/AppLayout.vue";
import AchievementsPage from "@/pages/AchievementsPage.vue";
import HomePage from "@/pages/HomePage.vue";
import LoginPage from "@/pages/LoginPage.vue";
import LogoutPage from "@/pages/LogoutPage.vue";
import NotificationsPage from "@/pages/NotificationsPage.vue";
import OnboardingPage from "@/pages/OnboardingPage.vue";
import ProfilePage from "@/pages/ProfilePage.vue";
import RedirectingPage from "@/pages/RedirectingPage.vue";
import RunsPage from "@/pages/RunsPage.vue";
import SeasonsPage from "@/pages/SeasonsPage.vue";
import SettingsPage from "@/pages/SettingsPage.vue";
import { RouteLocation, RouteLocationAsRelativeGeneric, RouteRecordRaw } from "vue-router";
import detectOS from "../utils/platform/detectOS";
import { OS } from "../utils/platform/detectOS.types";

export enum RouteName {
  account = "account",
  editProfile = "editProfile",
  feedback = "feedback",
  profile = "profile",
  achievements = "achievements",
  commish = "commish",
  commishPlayers = "commishPlayers",
  commishCourts = "commishCourts",
  commishEditRun = "commishEditRun",
  courtNew = "courtNew",
  courtsManage = "courtsManage",
  crew = "crew",
  crewNew = "crewNew",
  supporter = "supporter",
  home = "home",
  index = "index",
  install = "install",
  invitePlayer = "invitePlayer",
  joinCrew = "joinCrew",
  joinRun = "joinRun",
  login = "login",
  logout = "logout",
  notifications = "notifications",
  onboarding = "onboarding",
  onboardingTheme = "onboardingTheme",
  onboardingIntro = "onboardingIntro",
  onboardingStatuses = "onboardingStatuses",
  onboardingInvites = "onboardingInvites",
  onboardingNotifications = "onboardingNotifications",
  onboardingProfile = "onboardingProfile",
  player = "player",
  redirecting = "redirecting",
  run = "run",
  runNew = "runNew",
  runEdit = "runEdit",
  runInvite = "runInvite",
  runReview = "runReview",
  runPlayerStatus = "runPlayerStatus",
  schedule = "schedule",
  seasons = "seasons",
  season = "season",
  seasonNew = "seasonNew",
  seasonSchedule = "seasonSchedule",
  seasonEdit = "seasonEdit",
  seasonEditRoster = "seasonEditRoster",
  settings = "settings",
}

const EmptyRouteView = { render: () => null };

function redirectAsModal (backgroundRoute: RouteLocationAsRelativeGeneric) {
  return (to: RouteLocation) => ({
    ...backgroundRoute,
    hash: `#${to.path}`,
    params: {},
  });
}

export const routes: Array<RouteRecordRaw> = [
  {
    name: RouteName.login,
    path: "/login",
    component: LoginPage,
  },
  {
    name: RouteName.logout,
    path: "/logout",
    component: LogoutPage,
  },
  {
    name: RouteName.install,
    path: "/install",
    redirect () {
      switch (detectOS()) {
        case OS.iOS:
          window.location.href = "https://testflight.apple.com/join/ph4Exqn7";
          break;
        case OS.android:
          window.location.href = "https://play.google.com/store/apps/details?id=app.bball.prod&hl=en-US&ah=lSaUOraM74cPWHuKzL3wxDSISkc";
          break;
      }

      return {
        name: RouteName.index,
      };
    },
  },
  {
    name: RouteName.redirecting,
    path: "/redirecting",
    component: RedirectingPage,
  },
  {
    name: RouteName.index,
    path: "",
    redirect: {
      name: RouteName.home,
    },
  },
  {
    name: RouteName.onboarding,
    path: "/onboarding",
    component: OnboardingPage,
    redirect: (to: RouteLocation) => {
      return {
        name: RouteName.onboardingTheme,
        query: to.query,
      };
    },
    children: [
      {
        name: RouteName.onboardingTheme,
        path: "/onboarding/theme",
        component: EmptyRouteView,
      },
      {
        name: RouteName.onboardingIntro,
        path: "/onboarding/intro",
        component: EmptyRouteView,
      },
      {
        name: RouteName.onboardingStatuses,
        path: "/onboarding/statuses",
        component: EmptyRouteView,
      },
      {
        name: RouteName.onboardingInvites,
        path: "/onboarding/invites",
        component: EmptyRouteView,
      },
      {
        name: RouteName.onboardingNotifications,
        path: "/onboarding/notifications",
        component: EmptyRouteView,
      },
      {
        name: RouteName.onboardingProfile,
        path: "/onboarding/profile",
        component: EmptyRouteView,
      },
    ],
  },
  {
    name: RouteName.commish,
    path: "/commish",
    component: () => import("@/pages/commish/CommishPage.vue"),
    children: [
      {
        name: RouteName.commishPlayers,
        path: "/commish/players",
        component: () => import("@/pages/commish/CommishPlayersPage.vue"),
      },
      {
        name: RouteName.commishCourts,
        path: "/commish/courts",
        component: () => import("@/pages/commish/CommishCourtsPage.vue"),
      },
      {
        name: RouteName.commishEditRun,
        path: "/commish/runs/:runId",
        component: () => import("@/pages/commish/CommishEditRunPage.vue"),
      },
    ],
  },
  {
    path: "",
    component: AppLayout,
    children: [
      {
        path: "/home",
        component: HomePage,
        children: [
          {
            name: RouteName.home,
            path: "",
            component: RunsPage,
          },
          {
            name: RouteName.seasons,
            path: "/seasons",
            component: SeasonsPage,
          },
        ],
      },
      {
        name: RouteName.feedback,
        path: "/feedback",
        redirect: redirectAsModal({
          name: RouteName.home,
        }),
      },
      {
        name: RouteName.invitePlayer,
        path: "/invite",
        redirect: redirectAsModal({
          name: RouteName.notifications,
        }),
      },
      {
        name: RouteName.crewNew,
        path: "/crews/new",
        redirect: redirectAsModal({
          name: RouteName.home,
        }),
      },
      {
        name: RouteName.crew,
        path: "/crews/:crewId",
        redirect: redirectAsModal({
          name: RouteName.home,
        }),
        children: [
          {
            name: RouteName.joinCrew,
            path: "/crews/:crewId/join",
            redirect: redirectAsModal({
              name: RouteName.home,
            }),
          },
        ],
      },
      {
        name: RouteName.joinRun,
        path: "/run-invites/:runInviteId",
        redirect: redirectAsModal({
          name: RouteName.home,
        }),
      },
      {
        name: RouteName.player,
        path: "/players/:playerId",
        redirect: redirectAsModal({
          name: RouteName.home,
        }),
      },
      {
        name: RouteName.runNew,
        path: "/runs/new",
        redirect: redirectAsModal({
          name: RouteName.home,
        }),
      },
      {
        name: RouteName.run,
        path: "/runs/:runId",
        redirect: redirectAsModal({
          name: RouteName.home,
        }),
      },
      {
        name: RouteName.runEdit,
        path: "/runs/:runId/edit",
        redirect: redirectAsModal({
          name: RouteName.home,
        }),
      },
      {
        name: RouteName.runInvite,
        path: "/runs/:runId/invite",
        redirect: (to: RouteLocation) => ({
          name: RouteName.home,
          hash: `#/runs/${to.params.runId},${to.path}`,
          params: {},
        }),
      },
      {
        name: RouteName.runReview,
        path: "/runs/:runId/review",
        redirect: redirectAsModal({
          name: RouteName.home,
        }),
      },
      {
        name: RouteName.runPlayerStatus,
        path: "/runs/:runId/players/:playerId/status",
        redirect: (to: RouteLocation) => ({
          name: RouteName.home,
          hash: `#/runs/${to.params.runId},${to.path}`,
          params: {},
        }),
      },
      {
        name: RouteName.courtNew,
        path: "/courts/new",
        redirect: redirectAsModal({
          name: RouteName.home,
        }),
      },
      {
        name: RouteName.courtsManage,
        path: "/courts/manage",
        redirect: redirectAsModal({
          name: RouteName.home,
        }),
      },
      {
        name: RouteName.seasonNew,
        path: "/seasons/new",
        redirect: redirectAsModal({
          name: RouteName.home,
        }),
      },
      {
        name: RouteName.season,
        path: "/seasons/:seasonId",
        redirect: {
          name: RouteName.seasonSchedule,
        },
        children: [
          {
            name: RouteName.seasonSchedule,
            path: "/seasons/:seasonId/schedule",
            redirect: redirectAsModal({
              name: RouteName.seasons,
            }),
          },
          {
            name: RouteName.seasonEdit,
            path: "/seasons/:seasonId/edit",
            redirect: redirectAsModal({
              name: RouteName.seasons,
            }),
          },
          {
            name: RouteName.seasonEditRoster,
            path: "/seasons/:seasonId/roster/edit",
            redirect: redirectAsModal({
              name: RouteName.seasons,
            }),
          },
        ],
      },
      {
        name: RouteName.notifications,
        path: "/notifications",
        component: NotificationsPage,
      },
      {
        name: RouteName.account,
        path: "/account",
        redirect: {
          name: RouteName.profile,
        },
        component: AccountLayout,
        children: [
          {
            name: RouteName.profile,
            path: "/profile",
            component: ProfilePage,
          },
          {
            name: RouteName.editProfile,
            path: "/profile/edit",
            redirect: redirectAsModal({
              name: RouteName.profile,
            }),
          },
          {
            name: RouteName.achievements,
            path: "/achievements",
            component: AchievementsPage,
          },
          {
            name: RouteName.settings,
            path: "/settings",
            component: SettingsPage,
          },
        ],
      },
    ],
  },
  {
    name: RouteName.supporter,
    path: "/supporter",
    redirect: redirectAsModal({
      name: RouteName.profile,
    }),
  },
];
